import React, { useEffect, useState } from 'react';
import { MonitorSmartphone, LogOut, Loader2, AlertTriangle } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { getDeviceId } from '../lib/device';
import { useT } from '../context/LanguageContext';

// Stuurt een heartbeat voor dit apparaat. Bij apparaatlimiet toont het een blokkerende modal
// waarin de gebruiker een ander apparaat kan afmelden.
export default function DeviceGuard() {
  const { t } = useT();
  const [blocked, setBlocked] = useState(null); // { limit, devices: [] }
  const [busy, setBusy] = useState('');

  const heartbeat = async () => {
    try {
      await carsysApi.deviceHeartbeat({ deviceId: getDeviceId() });
      setBlocked(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 403 && d && d.error === 'device_limit') {
        setBlocked({ limit: d.limit, devices: d.devices || [] });
      }
    }
  };

  useEffect(() => {
    heartbeat();
    const iv = setInterval(heartbeat, 5 * 60 * 1000); // elke 5 min verversen
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const release = async (deviceId) => {
    setBusy(deviceId);
    try {
      await carsysApi.deleteDevice(deviceId);
      await heartbeat();
    } catch { /* noop */ }
    finally { setBusy(''); }
  };

  if (!blocked) return null;

  return (
    <div data-testid="device-limit-modal" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,.72)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 520, width: '100%', padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <AlertTriangle size={22} color="#dc2626" />
          <h2 style={{ margin: 0, fontSize: 19 }}>{t('Apparaatlimiet bereikt', 'Apparaatlimiet bereikt')}</h2>
        </div>
        <p style={{ fontSize: 13.5, color: '#475569', marginTop: 0 }}>
          {t('Je licentie staat maximaal', 'Je licentie staat maximaal')} <b>{blocked.limit}</b> {t('actieve apparaten toe. Meld een ander apparaat af om op dit apparaat verder te werken.', 'actieve apparaten toe. Meld een ander apparaat af om op dit apparaat verder te werken.')}
        </p>
        <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
          {blocked.devices.map(d => (
            <div key={d.deviceId} data-testid={`device-row-${d.deviceId}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 14px', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <MonitorSmartphone size={18} color="#64748b" style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{d.deviceName || t('Onbekend apparaat', 'Onbekend apparaat')}</div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                    {d.ip || '?'} · {t('laatst actief', 'laatst actief')} {d.lastSeen ? new Date(d.lastSeen).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                </div>
              </div>
              <button data-testid={`device-release-${d.deviceId}`} onClick={() => release(d.deviceId)} disabled={busy === d.deviceId}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', border: '1px solid #fecaca', borderRadius: 8, background: '#fff', color: '#dc2626', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {busy === d.deviceId ? <Loader2 size={13} className="spin" /> : <LogOut size={13} />} {t('Afmelden', 'Afmelden')}
              </button>
            </div>
          ))}
        </div>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
